import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Card,
  CardContent,
  CardActions,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  Badge,
  Stack,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Hotel as HotelIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  EventAvailable as EventAvailableIcon
} from '@mui/icons-material';

// Services API
import reservationService from '../../../services/reservationService';
import chambreService from '../../../services/chambreService';

// Helper functions pour datetime-local
const formatDateTimeLocal = (date) => {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const parseDateTimeLocal = (datetimeString) => {
  if (!datetimeString) return new Date();
  return new Date(datetimeString);
};

const formatDateDisplay = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const Reservations = () => {
  const [reservations, setReservations] = useState([]);
  const [chambres, setChambres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [openCheckInDialog, setOpenCheckInDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [selectedReservations, setSelectedReservations] = useState([]);
  
  // Filter states
  const [currentTab, setCurrentTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    statut: '',
    chambre: '',
    date_debut: '',
    date_fin: ''
  });

  // Form states
  const [formData, setFormData] = useState({
    chambre: '',
    nom_client: '',
    telephone_client: '',
    email_client: '',
    date_debut: formatDateTimeLocal(new Date()),
    date_fin: formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)), // +1 jour
    montant_acompte: '',
    notes: ''
  });

  // Load data function
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load reservations
      const response = await reservationService.getAll();
      console.log('API Response for reservations:', response);
      
      // Gérer différents formats de réponse
      let reservationData = [];
      if (Array.isArray(response)) {
        reservationData = response;
      } else if (response && Array.isArray(response.data)) {
        reservationData = response.data;
      } else if (response && response.results && Array.isArray(response.results)) {
        reservationData = response.results;
      } else if (response && typeof response === 'object') {
        reservationData = [response];
      }
      
      console.log('Processed reservations:', reservationData);
      setReservations(reservationData);
      
      // Load all chambres
      const chambreResponse = await chambreService.getAll();
      console.log('API Response for chambres:', chambreResponse);
      
      let chambreData = [];
      if (Array.isArray(chambreResponse)) {
        chambreData = chambreResponse;
      } else if (chambreResponse && Array.isArray(chambreResponse.data)) {
        chambreData = chambreResponse.data;
      } else if (chambreResponse && chambreResponse.results && Array.isArray(chambreResponse.results)) {
        chambreData = chambreResponse.results;
      } else if (chambreResponse && typeof chambreResponse === 'object') {
        chambreData = [chambreResponse];
      }
      
      console.log('Processed chambres:', chambreData);
      setChambres(chambreData);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // S'assurer que reservations est toujours un tableau
  const safeReservations = Array.isArray(reservations) ? reservations : [];

  // Filter reservations
  const filteredReservations = safeReservations.filter(reservation => {
    if (!reservation) return false;
    
    const matchesSearch = searchTerm === '' || 
      (reservation.nom_client && reservation.nom_client.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reservation.chambre?.numero && reservation.chambre.numero.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (reservation.telephone_client && reservation.telephone_client.includes(searchTerm)) ||
      (reservation.email_client && reservation.email_client.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatut = !filters.statut || reservation.statut === filters.statut;
    const matchesChambre = !filters.chambre || reservation.chambre?.id === parseInt(filters.chambre);
    
    // Filtrer par date
    let matchesDate = true;
    if (filters.date_debut && reservation.date_debut) {
      const filterDate = new Date(filters.date_debut);
      const reservationDate = new Date(reservation.date_debut);
      matchesDate = reservationDate >= filterDate;
    }
    
    return matchesSearch && matchesStatut && matchesChambre && matchesDate;
  });

  // Group reservations by status
  const reservationsEnAttente = filteredReservations.filter(r => r && r.statut === 'en_attente');
  const reservationsConfirmees = filteredReservations.filter(r => r && r.statut === 'confirmee');
  const reservationsEnCours = filteredReservations.filter(r => r && r.statut === 'en_cours');
  const reservationsTerminees = filteredReservations.filter(r => r && r.statut === 'terminee');
  const reservationsAnnulees = filteredReservations.filter(r => r && r.statut === 'annulee');

  // Handler functions
  const handleOpenDialog = (reservation = null) => {
    if (reservation) {
      setSelectedReservation(reservation);
      setFormData({
        chambre: reservation.chambre?.id || '',
        nom_client: reservation.nom_client || '',
        telephone_client: reservation.telephone_client || '',
        email_client: reservation.email_client || '',
        date_debut: reservation.date_debut ? formatDateTimeLocal(reservation.date_debut) : formatDateTimeLocal(new Date()),
        date_fin: reservation.date_fin ? formatDateTimeLocal(reservation.date_fin) : formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)),
        montant_acompte: reservation.montant_acompte || '',
        notes: reservation.notes || ''
      });
    } else {
      setSelectedReservation(null);
      setFormData({
        chambre: '',
        nom_client: '',
        telephone_client: '',
        email_client: '',
        date_debut: formatDateTimeLocal(new Date()),
        date_fin: formatDateTimeLocal(new Date(Date.now() + 24 * 60 * 60 * 1000)),
        montant_acompte: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedReservation(null);
  };

  const handleOpenCheckInDialog = (reservation) => {
    setSelectedReservation(reservation);
    setOpenCheckInDialog(true);
  };

  const handleCloseCheckInDialog = () => {
    setOpenCheckInDialog(false);
    setSelectedReservation(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      // Validate dates
      const dateDebut = parseDateTimeLocal(formData.date_debut);
      const dateFin = parseDateTimeLocal(formData.date_fin);
      
      if (dateFin <= dateDebut) {
        setError('La date de fin doit être après la date de début');
        return;
      }
      
      // Prepare data for API
      const submitData = {
        ...formData,
        date_debut: dateDebut.toISOString(),
        date_fin: dateFin.toISOString(),
        montant_acompte: parseFloat(formData.montant_acompte) || 0
      };
      
      if (selectedReservation) {
        // Update existing reservation
        await reservationService.update(selectedReservation.id, submitData);
        setSuccessMessage('Réservation modifiée avec succès');
      } else {
        // Create new reservation
        await reservationService.create(submitData);
        setSuccessMessage('Réservation créée avec succès');
      }
      
      await loadData();
      handleCloseDialog();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error saving reservation:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (id) => {
    try {
      setLoading(true);
      setError(null);
      
      await reservationService.update(id, { statut: 'confirmee' });
      setSuccessMessage('Réservation confirmée avec succès');
      
      await loadData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError('Erreur lors de la confirmation');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir annuler cette réservation ?')) {
      try {
        setLoading(true);
        setError(null);
        
        await reservationService.update(id, { statut: 'annulee' });
        setSuccessMessage('Réservation annulée avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de l\'annulation');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette réservation ?')) {
      try {
        setLoading(true);
        setError(null);
        
        await reservationService.delete(id);
        setSuccessMessage('Réservation supprimée avec succès');
        
        await loadData();
        
        setTimeout(() => setSuccessMessage(null), 3000);
        
      } catch (err) {
        setError('Erreur lors de la suppression');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCheckIn = async () => {
    if (!selectedReservation) return;
    
    try {
      setLoading(true);
      setError(null);
      
      // Convert reservation to location
      const locationData = {
        chambre: selectedReservation.chambre?.id,
        nom_client: selectedReservation.nom_client,
        telephone_client: selectedReservation.telephone_client,
        type_location: 'nuit', // Par défaut pour les réservations
        prix_convenu: 0, // À calculer selon la chambre
        notes: `Arrivée de la réservation #${selectedReservation.id}`,
        heure_debut: new Date().toISOString()
      };
      
      // Vous devrez appeler votre service de création de location ici
      console.log('Converting reservation to location:', locationData);
      
      // Mettre à jour le statut de la réservation
      await reservationService.update(selectedReservation.id, { statut: 'en_cours' });
      setSuccessMessage('Check-in effectué avec succès');
      
      await loadData();
      handleCloseCheckInDialog();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      console.error('Error during check-in:', err);
      setError('Erreur lors du check-in');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectReservation = (id) => {
    setSelectedReservations(prev => {
      if (prev.includes(id)) {
        return prev.filter(resId => resId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedReservations.length === filteredReservations.length) {
      setSelectedReservations([]);
    } else {
      setSelectedReservations(filteredReservations.map(r => r.id).filter(id => id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedReservations.length === 0) {
      setError('Veuillez sélectionner au moins une réservation');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const promises = selectedReservations.map(id => {
        switch (action) {
          case 'confirm':
            return reservationService.update(id, { statut: 'confirmee' });
          case 'cancel':
            return reservationService.update(id, { statut: 'annulee' });
          case 'delete':
            return reservationService.delete(id);
          default:
            return Promise.resolve();
        }
      });
      
      await Promise.all(promises);
      
      let message = '';
      switch (action) {
        case 'confirm': message = 'Réservations confirmées avec succès'; break;
        case 'cancel': message = 'Réservations annulées avec succès'; break;
        case 'delete': message = 'Réservations supprimées avec succès'; break;
      }
      
      setSuccessMessage(message);
      setSelectedReservations([]);
      
      await loadData();
      
      setTimeout(() => setSuccessMessage(null), 3000);
      
    } catch (err) {
      setError(`Erreur lors de l'action en masse: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Utility functions
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0 HTG';
    return new Intl.NumberFormat('fr-HT', {
      style: 'currency',
      currency: 'HTG',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const getStatutColor = (statut) => {
    switch (statut) {
      case 'en_attente': return 'warning';
      case 'confirmee': return 'info';
      case 'en_cours': return 'primary';
      case 'terminee': return 'success';
      case 'annulee': return 'error';
      default: return 'default';
    }
  };

  const getStatutText = (statut) => {
    switch (statut) {
      case 'en_attente': return 'En attente';
      case 'confirmee': return 'Confirmée';
      case 'en_cours': return 'En cours';
      case 'terminee': return 'Terminée';
      case 'annulee': return 'Annulée';
      default: return statut;
    }
  };

  const isReservationActive = (reservation) => {
    if (!reservation || !reservation.date_debut) return false;
    const now = new Date();
    const start = new Date(reservation.date_debut);
    const end = new Date(reservation.date_fin);
    return now >= start && now <= end && reservation.statut === 'confirmee';
  };

  // Render reservation card
  const renderReservationCard = (reservation) => {
    if (!reservation) return null;
    
    const isActive = isReservationActive(reservation);
    
    return (
      <Card key={reservation.id || Math.random()} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start">
            <Box sx={{ flex: 1 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Checkbox
                  size="small"
                  checked={selectedReservations.includes(reservation.id)}
                  onChange={() => handleSelectReservation(reservation.id)}
                />
                <Typography variant="h6">
                  <HotelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Chambre {reservation.chambre?.numero || 'N/A'}
                </Typography>
              </Box>
              
              <Stack spacing={1}>
                <Box display="flex" alignItems="center">
                  <PersonIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    {reservation.nom_client || 'Client non renseigné'}
                  </Typography>
                </Box>
                
                {reservation.telephone_client && (
                  <Box display="flex" alignItems="center">
                    <PhoneIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{reservation.telephone_client}</Typography>
                  </Box>
                )}
                
                {reservation.email_client && (
                  <Box display="flex" alignItems="center">
                    <EmailIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2">{reservation.email_client}</Typography>
                  </Box>
                )}
                
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    Du {formatDateDisplay(reservation.date_debut)}
                  </Typography>
                </Box>
                
                <Box display="flex" alignItems="center">
                  <CalendarTodayIcon sx={{ mr: 1, fontSize: 16 }} />
                  <Typography variant="body2">
                    Au {formatDateDisplay(reservation.date_fin)}
                  </Typography>
                </Box>
                
                {reservation.montant_acompte > 0 && (
                  <Box display="flex" alignItems="center">
                    <AttachMoneyIcon sx={{ mr: 1, fontSize: 16 }} />
                    <Typography variant="body2" color="primary">
                      Acompte: {formatCurrency(reservation.montant_acompte)}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
            
            <Box sx={{ minWidth: 100, textAlign: 'right' }}>
              <Chip
                label={getStatutText(reservation.statut)}
                color={getStatutColor(reservation.statut)}
                size="small"
                sx={{ mb: 1 }}
              />
              
              {isActive && (
                <Chip
                  label="Active maintenant"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ mb: 1 }}
                />
              )}
              
              <Typography variant="caption" display="block" color="text.secondary">
                {reservation.date_reservation ? 
                  `Réservé le ${formatDateDisplay(reservation.date_reservation)}` : 
                  'Date inconnue'}
              </Typography>
            </Box>
          </Box>
          
          {reservation.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              <InfoIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {reservation.notes}
            </Typography>
          )}
        </CardContent>
        
        <CardActions>
          {reservation.statut === 'confirmee' && (
            <Button
              size="small"
              color="success"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleOpenCheckInDialog(reservation)}
            >
              Check-in
            </Button>
          )}
          
          {reservation.statut === 'en_attente' && (
            <Button
              size="small"
              color="info"
              startIcon={<CheckIcon />}
              onClick={() => handleConfirm(reservation.id)}
            >
              Confirmer
            </Button>
          )}
          
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleOpenDialog(reservation)}
          >
            Modifier
          </Button>
          
          {reservation.statut !== 'annulee' && reservation.statut !== 'terminee' && (
            <Button
              size="small"
              color="error"
              startIcon={<CancelIcon />}
              onClick={() => handleCancel(reservation.id)}
            >
              Annuler
            </Button>
          )}
          
          {(reservation.statut === 'annulee' || reservation.statut === 'terminee') && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(reservation.id)}
            >
              Supprimer
            </Button>
          )}
        </CardActions>
      </Card>
    );
  };

  // Loading state
  if (loading && safeReservations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestion des Réservations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={loading}
        >
          Nouvelle Réservation
        </Button>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.light' }}>
            <Typography color="text.secondary" variant="body2">En attente</Typography>
            <Typography variant="h4" color="warning.dark">
              {reservationsEnAttente.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.light' }}>
            <Typography color="text.secondary" variant="body2">Confirmées</Typography>
            <Typography variant="h4" color="info.dark">
              {reservationsConfirmees.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.light' }}>
            <Typography color="text.secondary" variant="body2">En cours</Typography>
            <Typography variant="h4" color="primary.dark">
              {reservationsEnCours.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.light' }}>
            <Typography color="text.secondary" variant="body2">Terminées</Typography>
            <Typography variant="h4" color="success.dark">
              {reservationsTerminees.length}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'error.light' }}>
            <Typography color="text.secondary" variant="body2">Annulées</Typography>
            <Typography variant="h4" color="error.dark">
              {reservationsAnnulees.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Bulk Actions */}
      {selectedReservations.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.selected' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2">
              {selectedReservations.length} réservation(s) sélectionnée(s)
            </Typography>
            <Box display="flex" gap={1}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CheckIcon />}
                onClick={() => handleBulkAction('confirm')}
                disabled={loading}
              >
                Confirmer
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CloseIcon />}
                onClick={() => handleBulkAction('cancel')}
                disabled={loading}
              >
                Annuler
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => handleBulkAction('delete')}
                disabled={loading}
              >
                Supprimer
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setSelectedReservations([])}
              >
                Tout désélectionner
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Search and Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Rechercher client, chambre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Statut</InputLabel>
              <Select
                value={filters.statut}
                onChange={(e) => setFilters({ ...filters, statut: e.target.value })}
                label="Statut"
                disabled={loading}
              >
                <MenuItem value="">Tous</MenuItem>
                <MenuItem value="en_attente">En attente</MenuItem>
                <MenuItem value="confirmee">Confirmées</MenuItem>
                <MenuItem value="en_cours">En cours</MenuItem>
                <MenuItem value="terminee">Terminées</MenuItem>
                <MenuItem value="annulee">Annulées</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel>Chambre</InputLabel>
              <Select
                value={filters.chambre}
                onChange={(e) => setFilters({ ...filters, chambre: e.target.value })}
                label="Chambre"
                disabled={loading}
              >
                <MenuItem value="">Toutes</MenuItem>
                {Array.isArray(chambres) && chambres.map((chambre) => (
                  <MenuItem key={chambre.id} value={chambre.id}>
                    Chambre {chambre.numero}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              label="À partir du"
              type="date"
              value={filters.date_debut}
              onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })}
              InputLabelProps={{ shrink: true }}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" gap={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterListIcon />}
                onClick={() => setFilters({ statut: '', chambre: '', date_debut: '' })}
                disabled={loading}
              >
                Réinitialiser
              </Button>
              <Tooltip title="Actualiser">
                <IconButton onClick={loadData} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <Button
                variant="contained"
                onClick={() => {
                  const now = new Date();
                  setFilters({
                    ...filters,
                    date_debut: now.toISOString().split('T')[0]
                  });
                }}
                disabled={loading}
              >
                Aujourd'hui
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            label={
              <Badge badgeContent={reservationsEnAttente.length} color="warning" showZero>
                En attente
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={reservationsConfirmees.length} color="info" showZero>
                Confirmées
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={reservationsEnCours.length} color="primary" showZero>
                En cours
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={reservationsTerminees.length} color="success" showZero>
                Terminées
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={reservationsAnnulees.length} color="error" showZero>
                Annulées
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={filteredReservations.length} color="default" showZero>
                Toutes
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Reservations List */}
      <Box>
        {currentTab === 0 && (
          <>
            {reservationsEnAttente.length === 0 ? (
              <Alert severity="info">Aucune réservation en attente</Alert>
            ) : (
              reservationsEnAttente.map(renderReservationCard)
            )}
          </>
        )}
        
        {currentTab === 1 && (
          <>
            {reservationsConfirmees.length === 0 ? (
              <Alert severity="info">Aucune réservation confirmée</Alert>
            ) : (
              reservationsConfirmees.map(renderReservationCard)
            )}
          </>
        )}
        
        {currentTab === 2 && (
          <>
            {reservationsEnCours.length === 0 ? (
              <Alert severity="info">Aucune réservation en cours</Alert>
            ) : (
              reservationsEnCours.map(renderReservationCard)
            )}
          </>
        )}
        
        {currentTab === 3 && (
          <>
            {reservationsTerminees.length === 0 ? (
              <Alert severity="info">Aucune réservation terminée</Alert>
            ) : (
              reservationsTerminees.map(renderReservationCard)
            )}
          </>
        )}
        
        {currentTab === 4 && (
          <>
            {reservationsAnnulees.length === 0 ? (
              <Alert severity="info">Aucune réservation annulée</Alert>
            ) : (
              reservationsAnnulees.map(renderReservationCard)
            )}
          </>
        )}
        
        {currentTab === 5 && (
          <>
            {filteredReservations.length === 0 ? (
              <Alert severity="info">Aucune réservation trouvée</Alert>
            ) : (
              <>
                <Box mb={2}>
                  <Checkbox
                    checked={selectedReservations.length === filteredReservations.length}
                    indeterminate={selectedReservations.length > 0 && selectedReservations.length < filteredReservations.length}
                    onChange={handleSelectAll}
                  />
                  <Typography variant="body2" component="span">
                    Sélectionner tout ({filteredReservations.length} réservations)
                  </Typography>
                </Box>
                {filteredReservations.map(renderReservationCard)}
              </>
            )}
          </>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {selectedReservation ? 'Modifier la réservation' : 'Nouvelle réservation'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Chambre</InputLabel>
                  <Select
                    name="chambre"
                    value={formData.chambre}
                    onChange={handleInputChange}
                    label="Chambre"
                    disabled={!!selectedReservation || loading}
                  >
                    <MenuItem value="">Sélectionner une chambre</MenuItem>
                    {Array.isArray(chambres) && chambres.map((chambre) => (
                      <MenuItem key={chambre.id} value={chambre.id}>
                        Chambre {chambre.numero} - {chambre.type} ({chambre.prix_nuit} HTG/nuit)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="nom_client"
                  label="Nom du client"
                  value={formData.nom_client}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="telephone_client"
                  label="Téléphone"
                  value={formData.telephone_client}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="email_client"
                  label="Email"
                  type="email"
                  value={formData.email_client}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date_debut"
                  label="Date d'arrivée"
                  type="datetime-local"
                  value={formData.date_debut}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300, // 5 minutes
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="date_fin"
                  label="Date de départ"
                  type="datetime-local"
                  value={formData.date_fin}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 300,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  name="montant_acompte"
                  label="Acompte (HTG)"
                  type="number"
                  value={formData.montant_acompte}
                  onChange={handleInputChange}
                  InputProps={{ inputProps: { min: 0, step: 100 } }}
                  disabled={loading}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="notes"
                  label="Notes (préférences, demandes spéciales, etc.)"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} disabled={loading}>
              Annuler
            </Button>
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : (selectedReservation ? 'Modifier' : 'Créer')}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Check-in Dialog */}
      <Dialog open={openCheckInDialog} onClose={handleCloseCheckInDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Check-in - Réservation #{selectedReservation?.id}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Client: {selectedReservation?.nom_client || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    Chambre: {selectedReservation?.chambre?.numero || 'N/A'}
                  </Typography>
                  <Typography variant="body2">
                    Arrivée prévue: {formatDateDisplay(selectedReservation?.date_debut)}
                  </Typography>
                  <Typography variant="body2">
                    Départ prévu: {formatDateDisplay(selectedReservation?.date_fin)}
                  </Typography>
                </Box>
              </Alert>
            </Grid>
            
            <Grid item xs={12}>
              <Alert severity="warning">
                Cette action va convertir la réservation en location active.
                Le client pourra accéder à sa chambre immédiatement.
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCheckInDialog} disabled={loading}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<CheckCircleIcon />}
            onClick={handleCheckIn}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmer le check-in'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Reservations;